import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import StatusTimeline from '../components/StatusTimeline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { portalTranslations } from '../i18n/portalTranslations';
import { formatPortalDate, getShipmentsForClient, getStatusOrder } from '../services/shipmentService';
import { Shipment, ShipmentStatus } from '../types/shipment';
import './PortalPages.css';

const PortalPage: React.FC = () => {
  const { language } = useLanguage();
  const pt = portalTranslations[language].portal;
  const { user, loading, loginClient, logout, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ShipmentStatus | 'all'>('all');

  useEffect(() => {
    if (!user || user.role !== 'client') {
      setShipments([]);
      return undefined;
    }

    let active = true;
    setLoadingShipments(true);
    getShipmentsForClient(user.email)
      .then((rows) => {
        if (active) setShipments(rows);
      })
      .finally(() => {
        if (active) setLoadingShipments(false);
      });

    return () => {
      active = false;
    };
  }, [user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shipments.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!q) return true;
      return (
        s.declarationNumber.toLowerCase().includes(q) ||
        s.cargoDescription.toLowerCase().includes(q) ||
        s.border.toLowerCase().includes(q)
      );
    });
  }, [shipments, query, statusFilter]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setSubmitting(true);
    const result = await loginClient(email, password);
    setSubmitting(false);

    if (!result.ok) {
      if (result.error === 'not_client') {
        setError(
          language === 'ar'
            ? 'هذا حساب إدارة — سجّل الدخول من صفحة الإدارة: /admin'
            : 'This is an admin account — sign in at /admin'
        );
      } else {
        setError(language === 'ar' ? 'بيانات الدخول غير صحيحة' : 'Invalid credentials');
      }
    }
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setError(language === 'ar' ? 'أدخل بريدك أولاً' : 'Enter your email first');
      return;
    }
    setError('');
    setSubmitting(true);
    await resetPassword(email.trim());
    setSubmitting(false);
    setInfo(pt.resetSent);
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="container portal-inner portal-loading">
          {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <Seo
        title={`${pt.title} | الشراع`}
        description={pt.subtitle}
        path="/portal"
      />
      <div className="container portal-inner">
        <h1>{pt.title}</h1>
        <p className="portal-subtitle">{pt.subtitle}</p>

        {!user || user.role !== 'client' ? (
          <form className="portal-card portal-login" onSubmit={handleLogin}>
            <label>
              {pt.email}
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required dir="ltr" />
            </label>
            <label>
              {pt.password}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {error && <p className="portal-error">{error}</p>}
            {info && <p className="portal-info">{info}</p>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (language === 'ar' ? 'جاري الدخول...' : 'Signing in...') : pt.login}
            </button>
            <button type="button" className="portal-forgot" onClick={handleReset} disabled={submitting}>
              {pt.forgotPassword}
            </button>
            <p className="portal-demo">{pt.demoAccount}</p>
            <p className="portal-demo">
              {language === 'ar' ? (
                <>للإدارة: <Link to="/admin">/admin</Link></>
              ) : (
                <>Admin: <Link to="/admin">/admin</Link></>
              )}
            </p>
            <Link to="/" className="portal-back">← {language === 'ar' ? 'العودة للموقع' : 'Back to site'}</Link>
          </form>
        ) : (
          <div className="portal-dashboard">
            <div className="portal-dashboard-header">
              <p>{pt.welcome}، <strong>{user.name}</strong></p>
              <button type="button" className="btn btn-secondary" onClick={() => logout()}>{pt.logout}</button>
            </div>
            <h2>{pt.myShipments}</h2>

            <div className="portal-toolbar">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={pt.search}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ShipmentStatus | 'all')}
              >
                <option value="all">{pt.filterAll}</option>
                {getStatusOrder().map((s) => (
                  <option key={s} value={s}>{portalTranslations[language].tracker.steps[s]}</option>
                ))}
              </select>
            </div>

            {loadingShipments ? (
              <p className="portal-empty">{language === 'ar' ? 'جاري تحميل الشحنات...' : 'Loading shipments...'}</p>
            ) : filtered.length === 0 ? (
              <p className="portal-empty">{pt.noShipments}</p>
            ) : (
              <div className="portal-shipment-list">
                {filtered.map((shipment) => (
                  <article key={shipment.id} className="portal-shipment-card">
                    <div className="portal-shipment-head">
                      <span className="portal-declaration" dir="ltr">{shipment.declarationNumber}</span>
                      <span className="portal-status-badge">{portalTranslations[language].tracker.steps[shipment.status]}</span>
                    </div>
                    <p><strong>{pt.cargo}:</strong> {shipment.cargoDescription}</p>
                    <p><strong>{pt.border}:</strong> {shipment.border}</p>
                    <p className="portal-updated highlight-update">
                      {pt.updated}: {formatPortalDate(shipment.updatedAt, language)}
                    </p>
                    <StatusTimeline current={shipment.status} language={language} compact history={shipment.history} />
                    <Link className="btn btn-primary portal-detail-link" to={`/portal/shipments/${shipment.id}`}>
                      {pt.viewDetails}
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalPage;
